import React, { useState } from 'react'
import LegalPage from '../comps/legalPage'

const ROLES = [
  { value: 'support', label: 'Support' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'design', label: 'Design' },
  { value: 'creator-partnerships', label: 'Creator partnerships' },
  { value: 'community', label: 'Community' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'trust-safety', label: 'Trust and safety' },
  { value: 'other', label: 'Other' }
]

const empty = {
  name: '',
  email: '',
  phone: '',
  role: 'support',
  location: '',
  portfolio: '',
  message: ''
}

const Career = () => {
  const [form, setForm] = useState(empty)
  const [busy, setBusy] = useState(false)
  const [hint, setHint] = useState('')
  const [ok, setOk] = useState(false)

  const setField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }))
    setHint('')
  }

  const canSend = form.name.trim() && form.email.trim() && form.message.trim().length >= 20 && !busy

  const onSubmit = async (event) => {
    event.preventDefault()
    if (!canSend) return
    setBusy(true)
    setHint('')
    try {
      const res = await fetch('/api/careers/apply', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(form)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setHint(data.msg || 'Could not send. Try again.')
        return
      }
      setOk(true)
      setForm(empty)
    } catch (err) {
      setHint('Could not send. Check your connection.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <LegalPage
      title="Careers at Pinweelo"
      heading="Careers"
      description="Apply to work at Pinweelo. Send your details and we will review them in the control panel."
    >
      <div className="legalHeroShot legalRise">
        <img
          alt="Team working around a table"
          src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1600"
          referrerPolicy="no-referrer"
        />
        <div>
          <h2>Work at Pinweelo</h2>
          <p>
            We are a small team building a dark, fast place to watch and post. If you want to help with the product, creators, or the people who use it, apply below.
          </p>
        </div>
      </div>

      <article className="legalSplit legalRise">
        <img
          alt="People building a product on laptops"
          src="https://images.pexels.com/photos/3182773/pexels-photo-3182773.jpeg?auto=compress&cs=tinysrgb&w=1200"
          referrerPolicy="no-referrer"
        />
        <div>
          <h3>Product and engineering</h3>
          <p>
            Help ship the feed, watch, rooms, and studio. We care about speed, a clean dark UI, and tools that stay out of the way of the video.
          </p>
        </div>
      </article>

      <article className="legalSplit legalSplitFlip legalRise">
        <img
          alt="Creator filming a video"
          src="https://images.pexels.com/photos/3379942/pexels-photo-3379942.jpeg?auto=compress&cs=tinysrgb&w=1200"
          referrerPolicy="no-referrer"
        />
        <div>
          <h3>Creators and community</h3>
          <p>
            Work with posters, rooms, and partnerships so people can find work they like and talk about it. Support and trust keep the place usable.
          </p>
        </div>
      </article>

      <article className="legalSplit legalRise">
        <img
          alt="Design sketches on a desk"
          src="https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=1200"
          referrerPolicy="no-referrer"
        />
        <div>
          <h3>Design and brand</h3>
          <p>
            Shape how Pinweelo looks and feels: the black layout, the glass bits, and the moments that make posting and watching simple.
          </p>
        </div>
      </article>

      <div className="legalGallery legalRise">
        <img alt="Editing on a laptop" src="https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg?auto=compress&cs=tinysrgb&w=900" referrerPolicy="no-referrer" />
        <img alt="Studio lights and a camera" src="https://images.pexels.com/photos/3062541/pexels-photo-3062541.jpeg?auto=compress&cs=tinysrgb&w=900" referrerPolicy="no-referrer" />
        <img alt="Team talking in an office" src="https://images.pexels.com/photos/3184433/pexels-photo-3184433.jpeg?auto=compress&cs=tinysrgb&w=900" referrerPolicy="no-referrer" />
      </div>

      <div className="legalContact legalRise">
        <img
          alt="Person writing an application"
          src="https://images.pexels.com/photos/12211252/pexels-photo-12211252.jpeg?auto=compress&cs=tinysrgb&w=1400"
          referrerPolicy="no-referrer"
        />
        <form className="legalForm" onSubmit={onSubmit}>
          <h2>Apply</h2>
          <p>Tell us who you are and what you want to do. Applications show up in the Pinweelo control page.</p>
          <label>
            Full name
            <input
              type="text"
              name="name"
              autoComplete="name"
              placeholder="Your name"
              value={form.name}
              onChange={setField('name')}
            />
          </label>
          <label>
            Email
            <input
              type="email"
              name="email"
              autoComplete="email"
              placeholder="you@email.com"
              value={form.email}
              onChange={setField('email')}
            />
          </label>
          <label>
            Phone
            <input
              type="tel"
              name="phone"
              autoComplete="tel"
              placeholder="Optional"
              value={form.phone}
              onChange={setField('phone')}
            />
          </label>
          <label>
            Role
            <select name="role" value={form.role} onChange={setField('role')}>
              {ROLES.map((role) => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </label>
          <label>
            City
            <input
              type="text"
              name="location"
              autoComplete="address-level2"
              placeholder="Where you are based"
              value={form.location}
              onChange={setField('location')}
            />
          </label>
          <label>
            Portfolio or LinkedIn
            <input
              type="url"
              name="portfolio"
              placeholder="https://"
              value={form.portfolio}
              onChange={setField('portfolio')}
            />
          </label>
          <label>
            Why Pinweelo
            <textarea
              name="message"
              placeholder="What you have done, and what you want to do here."
              value={form.message}
              onChange={setField('message')}
            />
          </label>
          <button type="submit" disabled={!canSend}>{busy ? 'Sending…' : 'Send application'}</button>
          {ok ? <p className="legalOk">Got it. We will read your application in admin.</p> : null}
          {hint ? <p className="legalNote">{hint}</p> : null}
        </form>
      </div>

      <div className="legalGallery legalRise">
        <img alt="Headphones and a phone" src="https://images.pexels.com/photos/3756766/pexels-photo-3756766.jpeg?auto=compress&cs=tinysrgb&w=900" referrerPolicy="no-referrer" />
        <img alt="Desk with a laptop" src="https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&w=900" referrerPolicy="no-referrer" />
        <img alt="People watching a screen together" src="https://images.pexels.com/photos/799443/pexels-photo-799443.jpeg?auto=compress&cs=tinysrgb&w=900" referrerPolicy="no-referrer" />
      </div>
    </LegalPage>
  )
}

export default Career
